<script lang="ts">
  import { validateResetToken, resetPasswordWithToken } from '../../lib/api';
  import { getQueryParams } from '../../lib/router.svelte';
  import LoadingSpinner from '../ui/LoadingSpinner.svelte';

  // State
  let password = $state('');
  let confirmPassword = $state('');
  let showPassword = $state(false);
  let error = $state('');
  let success = $state(false);
  let loading = $state(false);

  // Token validation state
  let validatingToken = $state(true);
  let tokenValid = $state(false);
  let tokenError = $state('');
  let maskedEmail = $state('');

  // Get token from URL on mount
  const params = getQueryParams();
  const token = params.token || '';

  // Validate token on mount
  $effect(() => {
    if (token) {
      validateToken();
    } else {
      validatingToken = false;
      tokenError = 'No reset token provided. Please request a new password reset link.';
    }
  });

  async function validateToken() {
    validatingToken = true;
    tokenError = '';

    const result = await validateResetToken(token);

    if (result.valid) {
      tokenValid = true;
      maskedEmail = result.email || '';
    } else {
      tokenValid = false;
      tokenError = result.error || 'Invalid or expired reset link.';
    }

    validatingToken = false;
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    error = '';

    // Validate passwords match
    if (password !== confirmPassword) {
      error = 'Passwords do not match';
      return;
    }

    // Validate password length
    if (password.length < 8) {
      error = 'Password must be at least 8 characters';
      return;
    }

    loading = true;

    const result = await resetPasswordWithToken(token, password);

    if (result.success) {
      success = true;
    } else {
      error = result.error || 'Failed to reset password';
    }

    loading = false;
  }

  function navigateTo(path: string) {
    window.location.hash = path;
  }

  function togglePasswordVisibility() {
    showPassword = !showPassword;
  }
</script>

<div class="setpassword-container">
  <!-- Left Panel - Form -->
  <div class="form-panel">
    <div class="form-content">
      <!-- Logo and Header -->
      <div class="header-section">
        <div class="logo-container">
          <img src="/console/assets/app_logo.png" alt="EQUOS" class="logo-icon" />
          <span class="logo-text">EQUOS9</span>
        </div>

        {#if validatingToken}
          <h1>Verifying Link...</h1>
          <p class="subtitle">Please wait while we verify your reset link</p>
        {:else if !tokenValid}
          <h1>Link Expired</h1>
          <p class="subtitle">This password reset link is no longer valid</p>
        {:else if success}
          <h1>Password Reset!</h1>
          <p class="subtitle">Your password has been successfully updated</p>
        {:else}
          <h1>Set New Password</h1>
          <p class="subtitle">
            {#if maskedEmail}
              Create a new password for {maskedEmail}
            {:else}
              Create a new password for your account
            {/if}
          </p>
        {/if}
      </div>

      <!-- Loading State -->
      {#if validatingToken}
        <div class="loading-state">
          <LoadingSpinner size="large" />
          <p>Validating your reset link...</p>
        </div>
      {:else if !tokenValid}
        <!-- Token Invalid -->
        <div class="error-message" role="alert">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="8" x2="12" y2="12"/>
            <line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          <span>{tokenError}</span>
        </div>

        <button type="button" class="btn-primary" onclick={() => navigateTo('reset-password')}>
          <span>REQUEST NEW LINK</span>
        </button>
      {:else if success}
        <!-- Success State -->
        <div class="success-message" role="status">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          <div class="success-content">
            <strong>Password updated!</strong>
            <span>You can now sign in with your new password.</span>
          </div>
        </div>

        <button type="button" class="btn-primary" onclick={() => navigateTo('login')}>
          <span>SIGN IN NOW</span>
        </button>
      {:else}
        <!-- Password Form -->
        {#if error}
          <div class="error-message" role="alert">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"/>
              <line x1="12" y1="8" x2="12" y2="12"/>
              <line x1="12" y1="16" x2="12.01" y2="16"/>
            </svg>
            <span>{error}</span>
          </div>
        {/if}

        <form onsubmit={handleSubmit}>
          <div class="form-group">
            <label for="password">New Password</label>
            <div class="input-wrapper">
              <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                bind:value={password}
                required
                placeholder="Enter new password"
                disabled={loading}
                autocomplete="new-password"
                minlength="8"
              />
              <button
                type="button"
                class="password-toggle"
                onclick={togglePasswordVisibility}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {#if showPassword}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                {:else}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                    <circle cx="12" cy="12" r="3"/>
                  </svg>
                {/if}
              </button>
            </div>
            <p class="hint">Minimum 8 characters</p>
          </div>

          <div class="form-group">
            <label for="confirmPassword">Confirm Password</label>
            <div class="input-wrapper">
              <svg class="input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                bind:value={confirmPassword}
                required
                placeholder="Confirm new password"
                disabled={loading}
                autocomplete="new-password"
              />
            </div>
          </div>

          <button type="submit" class="btn-primary" disabled={loading}>
            {#if loading}
              <LoadingSpinner size="small" light />
              <span>Updating...</span>
            {:else}
              <span>SET NEW PASSWORD</span>
            {/if}
          </button>
        </form>
      {/if}

      <!-- Back to Login (always shown) -->
      <div class="links">
        <button
          type="button"
          class="back-link"
          onclick={() => navigateTo('login')}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          <span>Back to sign in</span>
        </button>
      </div>
    </div>
  </div>

  <!-- Right Panel - Branding -->
  <div class="brand-panel">
    <div class="brand-content">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>

      <div class="illustration">
        <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <circle cx="60" cy="60" r="50" fill="rgba(255,255,255,0.1)"/>
          <rect x="40" y="50" width="40" height="30" rx="4" stroke="white" stroke-width="2" fill="none"/>
          <circle cx="60" cy="65" r="8" stroke="white" stroke-width="2" fill="none"/>
          <path d="M60 68v5" stroke="white" stroke-width="2" stroke-linecap="round"/>
          <path d="M50 50V42a10 10 0 0 1 20 0v8" stroke="white" stroke-width="2" fill="none"/>
        </svg>
      </div>

      <h2 class="brand-title">Secure Password Reset</h2>
      <p class="brand-description">
        Choose a strong password to keep your account secure. We recommend using a mix of letters, numbers, and symbols.
      </p>
    </div>
  </div>
</div>

<style>
  .setpassword-container {
    min-height: 100vh;
    display: flex;
    flex-direction: row;
  }

  .form-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    background: #f8f9fc;
  }

  .form-content {
    width: 100%;
    max-width: 380px;
  }

  .header-section {
    text-align: center;
    margin-bottom: 2rem;
  }

  .logo-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }

  .logo-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
  }

  .logo-text {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f2937;
    letter-spacing: -0.02em;
  }

  h1 {
    font-size: 1.75rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 0.5rem;
  }

  .subtitle {
    font-size: 0.875rem;
    color: #6b7280;
    line-height: 1.5;
  }

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    padding: 2rem;
    color: #6b7280;
  }

  .success-message {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    background: #ecfdf5;
    border: 1px solid #a7f3d0;
    color: #047857;
    padding: 1rem;
    border-radius: 10px;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
    animation: slideIn 0.3s ease-out;
  }

  .success-message svg {
    width: 22px;
    height: 22px;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .success-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .success-content strong {
    font-weight: 600;
  }

  .success-content span {
    opacity: 0.9;
  }

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .error-message {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    background: #fef2f2;
    border: 1px solid #fecaca;
    color: #dc2626;
    padding: 0.875rem 1rem;
    border-radius: 10px;
    margin-bottom: 1.5rem;
    font-size: 0.875rem;
    animation: shake 0.5s ease-in-out;
  }

  .error-message svg {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
  }

  @keyframes shake {
    0%, 100% { transform: translateX(0); }
    20%, 60% { transform: translateX(-5px); }
    40%, 80% { transform: translateX(5px); }
  }

  form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }

  .form-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
  }

  .form-group label {
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
  }

  .hint {
    font-size: 0.75rem;
    color: #9ca3af;
    margin-top: 0.25rem;
  }

  .input-wrapper {
    position: relative;
    display: flex;
    align-items: center;
  }

  .input-icon {
    position: absolute;
    left: 1rem;
    width: 20px;
    height: 20px;
    color: #9ca3af;
    pointer-events: none;
    transition: color 0.2s ease;
  }

  .input-wrapper:focus-within .input-icon {
    color: #4A7DFF;
  }

  input[type="password"],
  input[type="text"] {
    width: 100%;
    padding: 0.875rem 3rem 0.875rem 3rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 1rem;
    background: white;
    color: #1f2937;
    transition: all 0.2s ease;
  }

  input::placeholder {
    color: #9ca3af;
  }

  input:focus {
    outline: none;
    border-color: #4A7DFF;
    box-shadow: 0 0 0 3px rgba(74, 125, 255, 0.1);
  }

  input:disabled {
    background: #f3f4f6;
    cursor: not-allowed;
  }

  .password-toggle {
    position: absolute;
    right: 1rem;
    background: none;
    border: none;
    cursor: pointer;
    padding: 0.25rem;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    transition: color 0.2s ease;
  }

  .password-toggle:hover {
    color: #4A7DFF;
  }

  .password-toggle svg {
    width: 20px;
    height: 20px;
  }

  .btn-primary {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.875rem 1.5rem;
    margin-top: 0.5rem;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    color: white;
    border: none;
    border-radius: 10px;
    font-size: 0.9375rem;
    font-weight: 600;
    letter-spacing: 0.05em;
    cursor: pointer;
    transition: all 0.2s ease;
  }

  .btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(74, 125, 255, 0.35);
  }

  .btn-primary:active:not(:disabled) {
    transform: translateY(0);
  }

  .btn-primary:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .links {
    margin-top: 1.5rem;
    display: flex;
    justify-content: center;
  }

  .back-link {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: none;
    border: none;
    color: #4A7DFF;
    font-size: 0.9375rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    padding: 0.5rem 1rem;
    border-radius: 8px;
  }

  .back-link:hover {
    background: rgba(74, 125, 255, 0.1);
  }

  .back-link svg {
    width: 18px;
    height: 18px;
  }

  .brand-panel {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(135deg, #4A7DFF 0%, #6B5CE7 100%);
    position: relative;
    overflow: hidden;
  }

  .brand-content {
    text-align: center;
    color: white;
    z-index: 1;
    padding: 2rem;
  }

  .circle {
    position: absolute;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.1);
  }

  .circle-1 {
    width: 300px;
    height: 300px;
    top: -100px;
    right: -50px;
  }

  .circle-2 {
    width: 200px;
    height: 200px;
    bottom: -50px;
    left: -50px;
  }

  .circle-3 {
    width: 150px;
    height: 150px;
    top: 50%;
    right: 10%;
    background: rgba(255, 255, 255, 0.05);
  }

  .illustration {
    width: 140px;
    height: 140px;
    margin: 0 auto 1.5rem;
  }

  .illustration svg {
    width: 100%;
    height: 100%;
  }

  .brand-title {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 1rem;
    letter-spacing: -0.02em;
  }

  .brand-description {
    font-size: 1rem;
    line-height: 1.6;
    opacity: 0.9;
    max-width: 300px;
    margin: 0 auto;
  }

  @media (max-width: 968px) {
    .setpassword-container {
      flex-direction: column-reverse;
    }

    .brand-panel {
      padding: 3rem 2rem;
      min-height: auto;
    }

    .illustration {
      width: 100px;
      height: 100px;
      margin-bottom: 1rem;
    }

    .brand-title {
      font-size: 1.5rem;
    }

    .brand-description {
      font-size: 0.875rem;
    }

    .circle-1 {
      width: 150px;
      height: 150px;
      top: -50px;
      right: -30px;
    }

    .circle-2 {
      width: 100px;
      height: 100px;
      bottom: -30px;
      left: -30px;
    }

    .circle-3 {
      display: none;
    }

    .form-panel {
      padding: 2rem 1.5rem 3rem;
    }
  }

  @media (max-width: 480px) {
    .form-content {
      max-width: 100%;
    }

    .logo-text {
      font-size: 1.25rem;
    }

    h1 {
      font-size: 1.5rem;
    }
  }
</style>
